import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Badge,
	Button,
	Card,
	CardBody,
	Page,
	Table,
	type TableColumn,
	useToast,
} from "@theg1group/ui";
import { useNavigate, useParams } from "react-router-dom";
import { AddressLink } from "../components/AddressLink.tsx";
import { ChainBadge } from "../components/ChainBadge.tsx";
import { api } from "../lib/api.ts";
import { fmtDate, fmtNum } from "../lib/format.ts";
import type { ClusterDetailData, ClusterEvidence, ClusterMember } from "../lib/types.ts";

export function ClusterDetail() {
	const { id = "" } = useParams();
	const navigate = useNavigate();
	const toast = useToast();
	const qc = useQueryClient();

	const detail = useQuery({
		queryKey: ["cluster", id],
		queryFn: () => api.get<ClusterDetailData>(`/api/clusters/${id}`),
		enabled: !!id,
	});

	const split = useMutation({
		mutationFn: (addressId: string) => api.post("/api/clusters/split", { addressId }),
		onSuccess: () => {
			toast.addToast("success", "Split queued — the cluster re-derives shortly");
			window.setTimeout(() => qc.invalidateQueries({ queryKey: ["cluster", id] }), 5_000);
		},
		onError: (e) => toast.addToast("error", String(e)),
	});

	const d = detail.data;
	if (detail.isLoading) return <Page title="Cluster">Loading…</Page>;
	if (detail.isError) return <Page title="Cluster">{String(detail.error)}</Page>;
	if (!d) return <Page title="Cluster">Not found.</Page>;

	const c = d.cluster;

	const memberCols: TableColumn<ClusterMember>[] = [
		{ id: "chain", header: "Chain", accessor: (m) => <ChainBadge chain={m.chain} /> },
		{
			id: "address",
			header: "Address",
			accessor: (m) => <AddressLink id={m.addressId} address={m.address} display={m.display} />,
		},
		{
			id: "rule",
			header: "Joined via",
			accessor: (m) => <Badge size="sm">{m.joinedRule.replaceAll("_", " ")}</Badge>,
		},
		{
			id: "conf",
			header: "Confidence",
			accessor: (m) => Number(m.confidence).toFixed(2),
		},
		{
			id: "labels",
			header: "Labels",
			accessor: (m) =>
				m.labels.length ? (
					<div className="flex flex-wrap gap-1">
						{m.labels.map((l) => (
							<Badge key={l} size="sm" variant="warning">
								{l}
							</Badge>
						))}
					</div>
				) : (
					"—"
				),
		},
		{
			id: "partners",
			header: "Partners",
			accessor: (m) =>
				m.partners.length ? (
					<span className="text-primary text-xs">{m.partners.join(", ")}</span>
				) : (
					"—"
				),
		},
		{
			id: "actions",
			header: "",
			accessor: (m) => (
				<Button
					size="sm"
					variant="ghost"
					loading={split.isPending && split.variables === m.addressId}
					onClick={() => split.mutate(m.addressId)}
				>
					Split out
				</Button>
			),
		},
	];

	const evidenceCols: TableColumn<ClusterEvidence>[] = [
		{
			id: "rule",
			header: "Rule",
			accessor: (e) => <Badge size="sm">{e.rule.replaceAll("_", " ")}</Badge>,
		},
		{
			id: "conf",
			header: "Confidence",
			accessor: (e) =>
				e.confidence < 0 ? (
					<span className="text-danger">veto ({e.confidence})</span>
				) : (
					Number(e.confidence).toFixed(2)
				),
		},
		{
			id: "a",
			header: "Address A",
			accessor: (e) => <AddrById id={e.addrAId} members={d.members} />,
		},
		{
			id: "b",
			header: "Address B",
			accessor: (e) => <AddrById id={e.addrBId} members={d.members} />,
		},
		{ id: "created", header: "Created", accessor: (e) => fmtDate(e.createdAt) },
		{ id: "by", header: "By", accessor: (e) => e.createdBy ?? "—" },
	];

	return (
		<Page
			title={
				<span className="inline-flex items-center gap-2">
					{c.name || `Cluster ${c.id.slice(0, 8)}…`}
					<ChainBadge chain={c.chainId} />
				</span>
			}
			description={`${fmtNum(c.size)} addresses · min confidence ${Number(c.minConfidence).toFixed(2)}`}
			actions={
				<Button
					variant="outline"
					onClick={() => navigate(`/graph?focus=${encodeURIComponent(`cluster:${c.id}`)}`)}
				>
					Focus in graph
				</Button>
			}
		>
			<Card className="mb-3.5">
				<CardBody>
					<h2 className="text-sm font-semibold text-content mb-3">Members ({d.members.length})</h2>
					<div className="overflow-x-auto">
						<Table
							columns={memberCols}
							data={d.members}
							keyExtractor={(m) => m.addressId}
							emptyMessage="No members."
						/>
					</div>
				</CardBody>
			</Card>
			<Card>
				<CardBody>
					<h2 className="text-sm font-semibold text-content mb-3">
						Evidence ({d.evidence.length})
					</h2>
					<div className="overflow-x-auto">
						<Table
							columns={evidenceCols}
							data={d.evidence}
							keyExtractor={(e) => e.id}
							emptyMessage="No evidence rows."
						/>
					</div>
				</CardBody>
			</Card>
		</Page>
	);
}

/** Resolve an evidence address id against the member list; fall back to the raw id. */
function AddrById({ id, members }: { id: string; members: ClusterMember[] }) {
	const m = members.find((x) => x.addressId === id);
	if (m) return <AddressLink id={m.addressId} address={m.address} display={m.display} />;
	return <AddressLink id={id} address={id} />;
}
